import { ThemeButton, ThemeInput, ThemeTextarea } from './ThemeWrapper';

function groupSuggestions(suggestions) {
  const groups = [];
  const index = new Map();
  suggestions.forEach((item) => {
    const name = item.groupLabel || 'Other';
    if (!index.has(name)) {
      const group = { name, items: [] };
      index.set(name, group);
      groups.push(group);
    }
    index.get(name).items.push(item);
  });
  return groups;
}

function Field({ label, required, hint, htmlFor, children }) {
  return (
    <div className="mb-4">
      <label htmlFor={htmlFor} className="block text-sm font-medium theme-text mb-1">
        {label}
        {required ? ' *' : ''}
      </label>
      {hint ? <p className="text-xs theme-text-secondary mb-2">{hint}</p> : null}
      {children}
    </div>
  );
}

export default function StudentAccessRequestForm({
  formData,
  submitting,
  error,
  success,
  disabled,
  suggestions = [],
  onChange,
  onPickSuggestion,
  submit,
}) {
  if (disabled) {
    return (
      <div className="p-3 rounded text-sm" style={{ backgroundColor: 'var(--color-error-100)', color: 'var(--color-error)' }}>
        {error}
      </div>
    );
  }

  const selected = new Set(formData.selectedObjectives || []);
  const groups = groupSuggestions(suggestions);
  const showGroupLabels = groups.length > 1;

  return (
    <form onSubmit={submit} noValidate>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="First name" required htmlFor="access-first">
          <ThemeInput
            id="access-first"
            name="firstName"
            autoComplete="given-name"
            value={formData.firstName}
            onChange={onChange}
            className="w-full"
            required
          />
        </Field>
        <Field label="Last name" required htmlFor="access-last">
          <ThemeInput
            id="access-last"
            name="lastName"
            autoComplete="family-name"
            value={formData.lastName}
            onChange={onChange}
            className="w-full"
            required
          />
        </Field>
      </div>

      <Field label="Email" required htmlFor="access-email">
        <ThemeInput
          id="access-email"
          type="email"
          name="email"
          autoComplete="email"
          value={formData.email}
          onChange={onChange}
          className="w-full"
          required
        />
      </Field>

      <Field label="Phone" required htmlFor="access-phone">
        <div className="flex">
          <span
            className="inline-flex items-center px-3 rounded-l-lg border-2 theme-text-secondary"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}
          >
            +91
          </span>
          <ThemeInput
            id="access-phone"
            name="phone"
            inputMode="numeric"
            autoComplete="tel"
            value={formData.phone}
            onChange={onChange}
            className="w-full rounded-l-none"
            maxLength={10}
            required
          />
        </div>
      </Field>

      <Field
        label="Objective"
        required
        htmlFor="access-objective"
        hint="Select all that apply, or type your own."
      >
        {suggestions.length ? (
          <>
            <div className="objective-suggestion-groups">
              {groups.map((group) => (
                <div key={group.name} className="objective-group">
                  {showGroupLabels ? <p className="objective-group-label">{group.name}</p> : null}
                  <div className="objective-suggestions" role="group" aria-label={`${group.name} suggestions`}>
                    {group.items.map((item) => (
                      <button
                        key={item.id || item.label}
                        type="button"
                        className="objective-suggestion"
                        aria-pressed={selected.has(item.label)}
                        onClick={() => onPickSuggestion(item.label)}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <p className="objective-selected-note" aria-live="polite">
              {selected.size
                ? `${selected.size} selected. Add a note below if you need to.`
                : 'Nothing selected yet.'}
            </p>
          </>
        ) : null}
        <ThemeTextarea
          id="access-objective"
          name="objective"
          rows={3}
          maxLength={1000}
          value={formData.objective}
          onChange={onChange}
          className="w-full"
          placeholder="Anything else?"
        />
      </Field>

      {error ? (
        <div className="mb-4 p-3 rounded text-sm" style={{ backgroundColor: 'var(--color-error-100)', color: 'var(--color-error)' }} role="alert">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="mb-4 p-3 rounded text-sm theme-card theme-text" role="status">
          {success}
        </div>
      ) : null}

      <ThemeButton type="submit" disabled={submitting}>
        {submitting ? 'Submitting…' : 'Submit'}
      </ThemeButton>
    </form>
  );
}
